/**
 * JWT token storage + helpers.
 *
 * Tokens live in localStorage. The api client reads `getAccessToken` on
 * every request and calls `refreshAccessToken` on a 401 before retrying.
 *
 * Sessions are bounded, not perpetual. Three things can end one:
 *   - **idle** — no interaction inside the window (`@/lib/idle`), enforced
 *     here by refusing to rotate, and server-side by the refresh token simply
 *     expiring;
 *   - **absolute cap** — the backend refuses to rotate a session older than
 *     `JWT_SESSION_MAX_HOURS` from the password entry that began it, so a
 *     password is re-entered at least once per cap regardless of activity;
 *   - **signing-key rotation** — ops changing `JWT_SIGNING_KEY` invalidates
 *     every token in the wild at once.
 * All three land in the same place: `endSession` → `/login`.
 */
import { clearActivity, isIdle, resetActivity } from "@/lib/idle";
import { isSiteGateRejection, redirectToSiteGate } from "@/lib/site-gate";

const ACCESS_KEY = "hriswb.access";
const REFRESH_KEY = "hriswb.refresh";
// Impersonation overlay: when a super admin acts as a client user, the minted
// short-lived token is stored here and takes precedence over the admin's own
// access token. The admin's real tokens stay untouched, so "stop" is just
// clearing this overlay — no re-login needed.
const IMPERSONATION_KEY = "hriswb.impersonation.access";
// Wall-clock (ms) when the current overlay was installed — powers the "acting
// as" banner's elapsed timer without a round-trip or clock-skew maths.
const IMPERSONATION_STARTED_KEY = "hriswb.impersonation.startedAt";

const API_URL = import.meta.env.VITE_API_URL || "";
const API_PREFIX = import.meta.env.VITE_API_PREFIX || "/api/v1";

function url(path: string): string {
  return `${API_URL}${API_PREFIX}${path}`;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

/** The token to authenticate with right now — the impersonation overlay if
 *  one is active, otherwise the user's own access token. */
export function getActiveAccessToken(): string | null {
  return localStorage.getItem(IMPERSONATION_KEY) ?? localStorage.getItem(ACCESS_KEY);
}

export function isImpersonating(): boolean {
  return localStorage.getItem(IMPERSONATION_KEY) !== null;
}

export function startImpersonationOverlay(accessToken: string): void {
  localStorage.setItem(IMPERSONATION_KEY, accessToken);
  localStorage.setItem(IMPERSONATION_STARTED_KEY, String(Date.now()));
}

/** Drop the overlay — the admin's own tokens are still in place. */
export function clearImpersonationOverlay(): void {
  localStorage.removeItem(IMPERSONATION_KEY);
  localStorage.removeItem(IMPERSONATION_STARTED_KEY);
}

/** Epoch-ms when the active impersonation overlay began, or null. Survives
 *  reloads; absent for sessions started before this was tracked (the banner
 *  falls back to the token's `iat`). */
export function getImpersonationStartedAt(): number | null {
  const raw = localStorage.getItem(IMPERSONATION_STARTED_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(IMPERSONATION_KEY);
  localStorage.removeItem(IMPERSONATION_STARTED_KEY);
  clearActivity();
}

export function isAuthenticated(): boolean {
  return getAccessToken() !== null;
}

export interface LoginResult {
  access: string;
  refresh: string;
}

export async function login(
  username: string,
  password: string,
): Promise<LoginResult> {
  const response = await fetch(url("/auth/token/"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    // Behind the site gate even the sign-in POST is refused, and no password
    // will ever work until the doorway passphrase is entered. Say so by going
    // there rather than showing "login failed" for a correct password.
    if (isSiteGateRejection(response)) {
      redirectToSiteGate();
    }
    const text = await response.text();
    throw new Error(text || `Login failed (${response.status})`);
  }
  const data = (await response.json()) as LoginResult;
  setTokens(data.access, data.refresh);
  // Seed the idle clock, unthrottled — without this a fresh session inherits
  // whatever stale timestamp the previous one left behind and could be judged
  // idle on its very first refresh.
  resetActivity();
  // Start the new session with a clean cache — guarantees fresh /me (identity,
  // role + entitlement flags) regardless of how the previous session ended.
  void import("@/lib/queryClient")
    .then((m) => m.queryClient.clear())
    .catch(() => {});
  return data;
}

/** Fired on `window` when the session is unrecoverable — the refresh token is
 *  missing, expired, or was rejected. `ProtectedRoute` listens and bounces to
 *  /login, so the user gets the sign-in screen instead of sitting on a shell
 *  where every poll raises a 401 toast. */
export const SESSION_EXPIRED_EVENT = "hriswb:session-expired";

/** Wipe the stored session and tell the app to send the user to /login. */
function endSession(): void {
  clearTokens();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
}

/** The refresh currently on the wire, shared by every caller.
 *
 *  The backend runs ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION, which
 *  makes a refresh token single-use: the first POST mints a new pair and
 *  blacklists the one it consumed. Without this guard a screen that polls
 *  several endpoints (the dashboard polls background jobs and notifications on
 *  the same tick) fires N simultaneous 401s → N refresh POSTs carrying the
 *  SAME token → the first succeeds and the rest come back rejected, tearing
 *  down the session that was just renewed. Sharing one promise means exactly
 *  one rotation per expiry. */
let inFlightRefresh: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

async function performRefresh(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) {
    endSession();
    return null;
  }
  // Inactivity ends the session here rather than letting a polling screen
  // rotate it. The server would eventually expire the refresh token anyway,
  // but only if nothing renewed it first — and something always does.
  if (isIdle()) {
    endSession();
    return null;
  }
  let response: Response;
  try {
    response = await fetch(url("/auth/token/refresh/"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
  } catch {
    // Network blip, not a dead session — keep the tokens so the next attempt
    // (or the next poll) can recover once connectivity returns.
    return null;
  }
  if (isSiteGateRejection(response)) {
    // Not an expired session — the doorway closed. Keep the tokens; they are
    // still good once the passphrase is re-entered.
    redirectToSiteGate();
    return null;
  }
  if (!response.ok) {
    // Another tab may have rotated the pair while we were on the wire; ours is
    // then merely the blacklisted predecessor, not an expired session.
    const current = getRefreshToken();
    if (current && current !== refresh) return getAccessToken();
    endSession();
    return null;
  }

  const data = (await response.json()) as { access: string; refresh?: string };
  if (data.refresh) {
    setTokens(data.access, data.refresh);
  } else {
    localStorage.setItem(ACCESS_KEY, data.access);
  }
  return data.access;
}

export function logout(): void {
  clearTokens();
  // Drop all cached per-user data (esp. /me role + entitlement flags) so the
  // NEXT login doesn't render with the previous account's identity — e.g.
  // logging in as `superadmin` after `admin` and not seeing the Platform
  // console / Roles page because a stale /me was still cached. Lazy import to
  // avoid an auth ↔ api ↔ queryClient module cycle.
  void import("@/lib/queryClient")
    .then((m) => m.queryClient.clear())
    .catch(() => {});
}
